import React from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useOverview } from "./../hooks/useOverview";
import { getOffers } from "../services/offers";
import { getWallet } from "../services/wallet";
import { getTransactions } from "../services/transactions";
import { getIntegrations } from "../services/integrations";

import {
    Users,
    ShoppingBag,
    IndianRupee,
    Gift,
    CreditCard,
    ArrowDown,
    ArrowUp,
    ChevronRight,
    RefreshCw,
    Tag,
} from "lucide-react";

import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { MobileHeader } from "./MobileHeader";

// import "./Overview.css";

function StatCard({
    title,
    value,
    subtitle,
    // change,
    icon: Icon,
    color,
}) {
    return (
        <div className="stat-card">

            <div className={`stat-icon ${color}`}>
                <Icon size={24} />
            </div>

            <div className="stat-content">

                <div className="stat-title">
                    {title}
                </div>

                <div className="stat-value">
                    {value}
                </div>

                <div className="stat-subtitle">
                    {subtitle}
                </div>

                {/* <div className="stat-change">
                    {change}
                </div> */}

            </div>

        </div>
    );
}

export function Overview() {
    const queryClient = useQueryClient();

    const navigate = useNavigate();

    const outletUid = localStorage.getItem(

        "selectedOutletUid"

    );

    const {
        data,
        isLoading,
        error,
    } = useOverview();

    console.log("Overview data:", data);

    useEffect(() => {

        if (!isLoading && data) {

            queryClient.prefetchQuery({

                queryKey: [

                    "offers",

                    outletUid,

                ],

                queryFn: () =>

                    getOffers(

                        outletUid

                    ),

            });

            queryClient.prefetchQuery({

                queryKey: [

                    "wallet",

                    outletUid,

                ],

                queryFn: () =>

                    getWallet(

                        outletUid

                    ),

            });

            queryClient.prefetchQuery({

                queryKey: [

                    "transactions",

                    outletUid,

                ],

                queryFn: () =>

                    getTransactions(

                        outletUid

                    ),

            });

            queryClient.prefetchQuery({

                queryKey: [

                    "integrations",

                    outletUid,

                ],

                queryFn: () =>

                    getIntegrations(

                        outletUid

                    ),

            });

        }

    }, [isLoading, data, queryClient, outletUid]);


    if (isLoading) {
        return (

            <div className="page-state">

                <div className="page-state-spinner" />

                <h2>

                    Loading...

                </h2>

                <p>

                    We're fetching your latest data.

                </p>

            </div>

        );
    }

    if (error) {
        return (

            <div className="page-state">

                <div className="page-state-icon">

                    !

                </div>

                <h2>

                    Something went wrong

                </h2>

                <p>

                    We couldn't load this page. Please try refreshing or come back in a moment.

                </p>

                <button

                    className="primary-btn"

                    onClick={() => window.location.reload()}

                >

                    Try Again

                </button>

            </div>

        );
    }

    const stats = [
        {
            title: "Customers Acquired",
            value: data.customersGained,
            subtitle: "All time",
            // change: "+5% from last month",
            icon: Users,
            color: "blue",
        },
        {
            title: "Redemptions",
            value: data.offersRedeemedAtThisBusiness,
            subtitle: "All time",
            // change: "+5% from last month",
            icon: ShoppingBag,
            color: "green",
        },
        {
            title: "Total Spend",
            value: `₹${data.totalSpend}`,
            subtitle: "All time",
            // change: "+5% from last month",
            icon: IndianRupee,
            color: "orange",
        },
        {
            title: "Referral Earnings",
            value: `₹${data.referralAmountEarned}`,
            subtitle: "All time",
            // change: "+5% from last month",
            icon: Gift,
            color: "purple",
        },
    ];

    return (

        <div className="overview-page">

            <MobileHeader />

            <Sidebar />

            <div className="overview-content">

                <Header
                    title="Overview"
                    subtitle="Here's what's happening with your account."
                    action={
                        <button className="primary-btn" onClick={() => navigate('/top-up-wallet')}>
                            <CreditCard size={18} />
                            Top Up Wallet
                        </button>
                    }
                />

                <div className="stats-grid">

                    {stats.map((item) => (
                        <StatCard
                            key={item.title}
                            {...item}
                        />
                    ))}

                </div>

                {/* Wallet Summary + Active Offers continue in Part 2 */}
                <div className="overview-grid">

                    {/* Wallet Summary */}

                    <div className="dashboard-card">

                        <div className="card-header">
                            <h2>Wallet Summary</h2>
                        </div>

                        <div className="wallet-content">

                            <div className="wallet-section">

                                <div className="wallet-label">

                                    <span>Balance</span>

                                    {/* <Info size={15} /> */}

                                </div>

                                <div className="wallet-value">
                                    ₹{data.walletBalance}
                                </div>

                                <p>
                                    Used for acquiring customers
                                </p>

                                <button className="primary-btn" onClick={() => navigate('/top-up-wallet')}>
                                    Top Up Wallet
                                </button>

                            </div>

                            <div className="wallet-divider" />

                            <div className="wallet-section">

                                <div className="wallet-label">

                                    <span>Referral Earnings</span>

                                    {/* <Info size={15} /> */}

                                </div>

                                <div className="wallet-value">
                                    ₹{data.referralAmountEarned}
                                </div>

                                <p>
                                    Earned from customer redemptions
                                </p>

                                <button className="secondary-btn">
                                    Manage Earnings
                                </button>

                            </div>

                        </div>

                        <div className="auto-topup">

                            <div className="auto-topup-left">

                                <RefreshCw size={18} />

                                <div>

                                    <strong>
                                        Auto Top-up Enabled
                                    </strong>

                                    <span>
                                        Wallet automatically tops up ₹5,000 when balance falls below ₹500.
                                    </span>

                                </div>

                            </div>

                            <button className="text-button">
                                Manage
                                <ChevronRight size={16} />
                            </button>

                        </div>

                    </div>

                    {/* Active Offers */}

                    <div className="dashboard-card">

                        <div className="card-header">

                            <h2>Active Offers</h2>

                            <button
                                className="text-button"
                                onClick={() => navigate("/offers")}
                            >
                                View All
                                <ChevronRight size={16} />
                            </button>

                        </div>

                        <div className="active-offers-main">

                            <div className="offer-icon">

                                <Tag size={28} />

                            </div>

                            <div>

                                <div className="offer-count">

                                    <strong>{data.activeOffers}</strong>

                                    <span>Active Offers</span>

                                </div>

                                <p>
                                    Currently running across the network
                                </p>

                            </div>

                        </div>

                        <div className="offer-metrics">

                            <div className="metric">

                                <span>Redemptions</span>

                                <strong>{data.offersRedeemedAtThisBusiness}</strong>

                            </div>

                            <div className="metric">

                                <span>Customers</span>

                                <strong>{data.customersGained}</strong>

                            </div>

                            <div className="metric">

                                <span>Spend</span>

                                <strong>₹{data.totalSpend}</strong>

                            </div>

                        </div>

                    </div>

                </div>

                {/* Recent Activity */}

                <div className="dashboard-card recent-card">

                    <div className="card-header">

                        <h2>Recent Activity</h2>

                        <button className="text-button">
                            View All
                            <ChevronRight size={16} />
                        </button>

                    </div>

                    <table className="activity-table">

                        <thead>

                            <tr>

                                <th>Activity</th>

                                <th>Date</th>

                                <th>Amount</th>

                                <th>Balance</th>

                            </tr>

                        </thead>

                        <tbody>

                            {data.recentHistory.map((item) => {

                                let iconClass = "";
                                let amountClass = "";

                                switch (item.type) {

                                    case "topup":
                                        iconClass = "upBlue";
                                        amountClass = "positiveBlue";
                                        break;

                                    case "referral":
                                        iconClass = "up";
                                        amountClass = "positive";
                                        break;

                                    case "redemption":
                                        iconClass = "down";
                                        amountClass = "negative";
                                        break;

                                    case "withdrawal":
                                        iconClass = "downAmber";
                                        amountClass = "withdrawal";
                                        break;

                                    default:

                                        iconClass =
                                            item.amount < 0
                                                ? "down"
                                                : "up";

                                        amountClass =
                                            item.amount < 0
                                                ? "negative"
                                                : "positive";

                                }

                                const Icon =
                                    item.amount < 0
                                        ? ArrowDown
                                        : ArrowUp;

                                return (

                                    <tr key={item.id}>

                                        <td>

                                            <div className="activity-cell">

                                                <div
                                                    className={`activity-icon ${iconClass}`}
                                                >

                                                    <Icon size={18} />

                                                </div>

                                                <div>

                                                    <strong>
                                                        {item.type}
                                                    </strong>

                                                    <p>
                                                        {item.title}
                                                    </p>

                                                </div>

                                            </div>

                                        </td>

                                        <td>

                                            {new Date(item.createdAt).toLocaleString("en-IN", {
                                                day: "2-digit",
                                                month: "short",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}

                                        </td>

                                        <td className={amountClass}>

                                            {item.amount >= 0
                                                ? `+₹${item.amount}`
                                                : `-₹${Math.abs(item.amount)}`}

                                        </td>

                                        <td>

                                            <strong>
                                                ₹{item.balanceAfter}
                                            </strong>

                                        </td>

                                    </tr>

                                );

                            })}
                        </tbody>

                    </table>

                </div>

            </div>

        </div>
    );
}